// Contracts — tenant-wide roll-up of funding agreements.
//
// Contracts are SENT from each client's page (percentage picker + Send). This
// page is the overview: the percentage tiers you can charge, and every
// agreement you've sent with its status.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Eye, FileText, FileCheck2, FileClock, Pencil, Percent, Plus, Trash2 } from "lucide-react";
import { Modal } from "../components/Modal";
import { Shell } from "../components/Shell";
import { StatCard } from "../components/StatCard";
import { contractTemplates as tplApi, openAuthenticatedFile, request, type ContractTemplate } from "../lib/api";

const PERCENTAGES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 9.99, 10];

interface Agreement {
  id: string;
  client_id: string;
  client_name: string;
  backend_percentage: number | null;
  sent_to_email: string | null;
  sent_at: string | null;
  signed_at: string | null;
  status: "signed" | "pending" | "expired" | "revoked";
}

const STATUS_PILL: Record<Agreement["status"], string> = {
  signed: "pill-green",
  pending: "",
  expired: "pill-red",
  revoked: "pill-red",
};

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

export default function ContractsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Agreement[]>([]);
  const [summary, setSummary] = useState<{ total: number; signed: number; pending: number }>({ total: 0, signed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Open the stored agreement document (exactly what the client saw/signed)
  // in a new tab, via authenticated fetch → blob URL.
  async function viewAgreement(id: string) {
    try { await openAuthenticatedFile(`/api/agreements/${id}/html`, "contract.html"); }
    catch (e: any) { setErr(e?.message || "Could not open the contract."); }
  }

  // contract templates
  const [tpls, setTpls] = useState<ContractTemplate[]>([]);
  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState<ContractTemplate | null>(null);
  useEffect(() => { tplApi.list().then(setTpls).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await request<{ ok: true; agreements: Agreement[]; summary: any }>("/api/agreements");
        setRows(r.agreements);
        setSummary(r.summary);
      } catch (e: any) {
        setErr(e?.message || "Could not load contracts.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signRate = useMemo(
    () => (summary.total ? Math.round((summary.signed / summary.total) * 100) : 0),
    [summary],
  );

  return (
    <Shell title="Contracts" subtitle="Funding agreements & back-end percentages">
      {/* summary */}
      <section className="stats-grid" style={{ marginBottom: 18 }}>
        <StatCard label="Agreements sent" value={String(summary.total)} detail="all time" icon={<FileText size={18} aria-hidden="true" />} />
        <StatCard label="Signed" value={String(summary.signed)} detail="executed" icon={<FileCheck2 size={18} aria-hidden="true" />} />
        <StatCard label="Awaiting signature" value={String(summary.pending)} detail="pending" icon={<FileClock size={18} aria-hidden="true" />} />
        <StatCard label="Sign rate" value={`${signRate}%`} detail="signed / sent" icon={<Percent size={18} aria-hidden="true" />} />
      </section>

      {/* percentage tiers reference */}
      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-header">
          <div>
            <h2>Your back-end percentages</h2>
            <span className="muted small">
              The percentage tiers you can send — pick per client from their page.
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PERCENTAGES.map((p) => (
            <span key={p} className="pill" style={{ fontSize: 15, padding: "8px 16px" }}>{p}%</span>
          ))}
        </div>
      </div>

      {/* contract templates — customizable per state / language / percentage */}
      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-header">
          <div>
            <h2>Contract templates</h2>
            <span className="muted small">
              Customize the written contract per state, language, or percentage. Every template keeps your branded document look.
            </span>
          </div>
          <button className="btn-primary" onClick={() => { setEditingTpl(null); setTplModalOpen(true); }}>
            <Plus size={16} aria-hidden="true" /> New contract template
          </button>
        </div>
        {tpls.length === 0 ? (
          <p className="muted small">Loading templates…</p>
        ) : (
          <div className="table-list">
            {tpls.map((t) => (
              <div key={t.id} className="table-row table-row--wide" style={{ flexWrap: "wrap", gap: "8px 14px", alignItems: "center" }}>
                <strong style={{ flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere" }}>
                  {t.name}{t.is_default && <span className="pill pill-green" style={{ marginLeft: 8, fontSize: 10 }}>DEFAULT</span>}
                </strong>
                <span className="muted small" style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>
                  {t.backend_percentage != null ? `${t.backend_percentage}%` : "any %"} · {t.jurisdiction_state || "default state"} · {t.language}
                </span>
                <span style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
                  <button className="ghost-button" title="Preview" onClick={() => tplApi.openPreview({ body_html: t.body_html, name: t.name, percentage: t.backend_percentage, jurisdiction_state: t.jurisdiction_state }).catch((e) => setErr(e.message))}>
                    <Eye size={14} aria-hidden="true" />
                  </button>
                  <button className="ghost-button" title="Edit" onClick={() => { setEditingTpl(t); setTplModalOpen(true); }}>
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  <button className="ghost-button" title="Delete" style={{ color: "var(--ne-red)" }}
                    onClick={async () => {
                      if (!confirm(`Delete template "${t.name}"?`)) return;
                      try { await tplApi.remove(t.id); setTpls(prev => prev.filter(x => x.id !== t.id)); }
                      catch (e: any) { setErr(e?.message || "Delete failed"); }
                    }}>
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {tplModalOpen && (
        <TemplateEditorModal
          template={editingTpl}
          onClose={() => setTplModalOpen(false)}
          onSaved={(saved) => {
            setTpls(prev => {
              const cleared = saved.is_default ? prev.map(p => ({ ...p, is_default: false })) : prev;
              const i = cleared.findIndex(p => p.id === saved.id);
              return i >= 0 ? cleared.map(p => (p.id === saved.id ? saved : p)) : [...cleared, saved];
            });
            setTplModalOpen(false);
          }}
        />
      )}

      {/* sent agreements */}
      <div className="panel">
        <div className="panel-header"><div><h2>Sent agreements</h2></div></div>
        {loading && <p className="muted small">Loading…</p>}
        {err && <p className="error-text">{err}</p>}
        {!loading && !err && rows.length === 0 && (
          <p className="muted small">
            No contracts sent yet. Open a client and use the contract picker to send their funding agreement.
          </p>
        )}
        {rows.length > 0 && (
          <div className="table-list">
            {rows.map((a) => (
              <button
                key={a.id}
                className="table-row table-row--wide"
                // table-row--wide is the flex variant — these rows
                // have their own shape (name / % / status / dates). Under the grid the
                // name fell into a 64px track (truncated) and the pill overlapped the date.
                style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", alignItems: "center", textAlign: "left", cursor: "pointer", width: "100%", background: "none", border: "none", borderBottom: "1px solid var(--ne-line)" }}
                onClick={() => nav(`/clients/${a.client_id}`)}
              >
                <strong style={{ flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere" }}>{a.client_name}</strong>
                <span style={{ flex: "0 0 auto", minWidth: 48, fontWeight: 700 }}>{a.backend_percentage != null ? `${a.backend_percentage}%` : "—"}</span>
                <span className={`pill ${STATUS_PILL[a.status]}`} style={{ flex: "0 0 auto", whiteSpace: "nowrap", display: "inline-flex", gap: 5, alignItems: "center", justifyContent: "center" }}>
                  {a.status === "signed" ? <CheckCircle2 size={13} aria-hidden="true" />
                    : a.status === "pending" ? <Clock size={13} aria-hidden="true" />
                    : <XCircle size={13} aria-hidden="true" />}
                  {a.status}
                </span>
                <span className="muted small" style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>Sent {fmt(a.sent_at)}</span>
                <span className="muted small" style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>{a.signed_at ? `Signed ${fmt(a.signed_at)}` : ""}</span>
                <span
                  role="button"
                  tabIndex={0}
                  className="btn-secondary"
                  style={{ flex: "0 0 auto", whiteSpace: "nowrap", height: 32, padding: "0 12px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}
                  onClick={(e) => { e.stopPropagation(); viewAgreement(a.id); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); viewAgreement(a.id); } }}
                  aria-label={`View contract for ${a.client_name}`}
                >
                  <FileText size={13} aria-hidden="true" /> View contract
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

/**
 * Template editor — the written contract with {{tokens}}, plus the fields
 * that vary per deal: percentage, governing-law state, language. Preview
 * renders with sample client data inside the real branded document shell.
 */
function TemplateEditorModal({ template, onClose, onSaved }: {
  template: ContractTemplate | null;
  onClose: () => void;
  onSaved: (t: ContractTemplate) => void;
}) {
  const isNew = !template;
  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [pct, setPct] = useState(template?.backend_percentage != null ? String(template.backend_percentage) : "10");
  const [state, setState] = useState(template?.jurisdiction_state || "");
  const [language, setLanguage] = useState(template?.language || "English");
  const [bodyHtml, setBodyHtml] = useState(template?.body_html || "");
  const [isDefault, setIsDefault] = useState(template?.is_default || false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Start from the standard agreement" — copy the default template's body.
  async function loadStandardBody() {
    try {
      const list = await tplApi.list();
      const std = list.find(t => t.is_default) || list[0];
      if (std) setBodyHtml(std.body_html);
    } catch (e: any) { setError(e?.message || "Could not load the standard body."); }
  }

  async function save() {
    setError(null);
    if (!name.trim()) return setError("Give the template a name.");
    if (bodyHtml.trim().length < 40) return setError("The contract body is empty — write it or start from the standard agreement.");
    const pctNum = pct.trim() === "" ? null : Number(pct);
    if (pctNum !== null && (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 100)) return setError("Percentage must be between 0 and 100 (or blank).");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), description: description || null, body_html: bodyHtml,
        backend_percentage: pctNum, jurisdiction_state: state || null,
        language: language || "English", is_default: isDefault,
      };
      const saved = isNew ? await tplApi.create(payload as any) : await tplApi.update(template!.id, payload);
      onSaved(saved);
    } catch (e: any) {
      setError(e?.message || "Could not save the template.");
    } finally { setBusy(false); }
  }

  return (
    <Modal open title={isNew ? "New contract template" : `Edit template — ${template!.name}`} onClose={onClose} wide
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-secondary" onClick={() => tplApi.openPreview({ body_html: bodyHtml, name, percentage: pct ? Number(pct) : null, jurisdiction_state: state || null }).catch((e) => setError(e.message))} disabled={busy || bodyHtml.trim().length < 40}>
            <Eye size={14} aria-hidden="true" /> Preview
          </button>
          <button className="btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save template"}</button>
        </>
      }
    >
      {error && <p className="error-text">{error}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
        <label className="form-field"><span>Template name *</span>
          <input className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Texas 8% agreement" />
        </label>
        <label className="form-field"><span>Fee percentage</span>
          <input className="text-input" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="10" inputMode="decimal" />
        </label>
        <label className="form-field"><span>Governing-law state</span>
          <input className="text-input" value={state} onChange={(e) => setState(e.target.value)} placeholder="Louisiana" />
        </label>
        <label className="form-field"><span>Language</span>
          <input className="text-input" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="English" />
        </label>
      </div>
      <label className="form-field" style={{ marginBottom: 10 }}><span>Description</span>
        <input className="text-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When to use this template" />
      </label>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <span className="muted small">
          Contract body — placeholders fill automatically: {"{{client_name}} {{client_address}} {{client_email}} {{company}} {{company_state}} {{percentage}} {{date}}"}
        </span>
        {isNew && <button className="ghost-button" onClick={loadStandardBody}>Start from the standard agreement</button>}
      </div>
      <textarea className="text-input" value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
        style={{ width: "100%", minHeight: 320, fontFamily: "Menlo, monospace", fontSize: 12, lineHeight: 1.5 }}
        placeholder="Write or paste the contract text here. Basic HTML (<h2>, <p>, <ul>) controls the layout; use the placeholders above for client and deal details." />
      <label className="muted small" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 10 }}>
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
        Make this the default template for new contracts
      </label>
    </Modal>
  );
}
